/**
 * The *editable* form of the optimization problem: the rules and the objective's
 * cost categories, each kept as text, plus the compile step that turns them into
 * the ASTs the interpreter grounds.
 *
 * Text is the source of truth, not the AST. That's what makes live editing
 * work: a half-typed rule is still a rule in the list — it just doesn't compile
 * yet, so it's left out of the model and its error is shown in the editor
 * instead. Compiling is parse + schema validation, per entry, and one bad entry
 * never stops the others from grounding.
 *
 * A rule's *kind* is read off its body rather than declared: a ranged body
 * (`0 <= P_charge(b, t) <= b.max_charge`) declares a decision variable and its
 * bounds, anything else is a class of constraint rows. So changing a rule from
 * `==` to a two-sided bound moves it from the constraints section to the
 * variables section on its own.
 *
 * The objective is `minimize cost` over the sum of its terms. The sense and the
 * name are fixed; the terms — the cost categories — are the editable part, each
 * a parameterless definition that adds and removes like a rule does.
 */

import { Model, ObjectiveTermGroup } from '../highs';
import { Catalog, catalogFor, GRID_DATA_CATALOG } from './schema';
import { typedCatalog } from './types';
import { Rule, Objective, Expr, num, objective as makeObjective, ruleToString, costTermToString } from './ruleAst';
import { parseRule, parseRuleWithSpans, parseCostTermWithSpans, ParseError } from './parse';
import { Diagnostic, Span, locate, messagesOf } from './diagnostics';
import { validateRuleDiagnostics } from './validate';
import { groundVariables, groundRules, groundObjective, groundDerived, Data } from './interpret';
import { ALL_RULES, COST_TERMS, finalSocRule, finalSocMatchRule } from './constraintRules';

// ---------------------------------------------------------------------------
// Source form — what the UI stores and the user edits
// ---------------------------------------------------------------------------

/**
 * A named, individually toggleable piece of the problem, held as the text the
 * user types. Shared by rules and cost categories: both are named, both compile
 * or don't, both can be switched off without being deleted.
 *
 * The **name is in the text** — a rule reads `def dcpf(l: line, …):`, a cost
 * category `def generation():` — so renaming is just an edit. That's why `id` exists
 * separately: something has to say *which* entry an edit lands on, and it can't
 * be the name if the name is what's being edited. `id` is fixed when the entry
 * is created and never changes.
 */
export interface SourceEntry {
  /** Stable identity, fixed at creation. Also the id of the entry's panel. */
  id: string;
  /**
   * The name the text last parsed to: the group its grounded rows carry and its
   * label in the tree. Cached rather than derived on the spot so a rule keeps
   * its identity in the UI through the keystrokes where its text doesn't parse.
   */
  name: string;
  /** The text, exactly as typed. */
  source: string;
  /** A disabled entry is kept in the list but left out of the model. */
  enabled: boolean;
}

/** One rule: the `def <name>(<params>): <body>` text. */
export type RuleSource = SourceEntry;

/**
 * One cost category: `def <name>(): <expr>` — a single summand of the objective,
 * with no parameters and no `minimize` head of its own.
 */
export type CostSource = SourceEntry;

/** The whole editable problem: the rules and the objective's cost categories. */
export interface RuleSetSource {
  rules: RuleSource[];
  costs: CostSource[];
}

// ---------------------------------------------------------------------------
// Compiled form
// ---------------------------------------------------------------------------

/**
 * What every compiled entry carries about its problems: the messages, and the
 * same problems with the source spans an editor underlines.
 */
interface Compiled {
  /** Syntax and schema errors, empty when the entry compiled cleanly. */
  errors: string[];
  /** The same errors, each located in the entry's text where that's known. */
  diagnostics: Diagnostic[];
  /** The extent of the name in the `def` head, when the text got that far. */
  nameSpan?: Span;
}

/** A rule after parsing + validation: the AST, or the errors that stopped it. */
export interface CompiledRule extends RuleSource, Compiled {
  /** The parsed rule, absent when `errors` is non-empty. */
  rule?: Rule;
  /**
   * For a ranged rule, the relation whose variables it declares (`P_charge`) —
   * the group its variables are listed under. Absent for comparison rules.
   */
  declares?: string;
  /**
   * For a definition rule, the data relation it computes rows of — which is its
   * own name. Absent for every other kind.
   */
  defines?: string;
}

/** A cost category after parsing + validation: its expression, or its errors. */
export interface CompiledCost extends CostSource, Compiled {
  /** The parsed expression, absent when `errors` is non-empty. */
  expr?: Expr;
}

/** The rules and costs that actually go into the model, split the way grounding wants them. */
export interface RuleProgram {
  /** Definition rules: each computes rows of a derived data relation. */
  derivedRules: Rule[];
  /** Ranged rules: each declares a decision variable and its bounds. */
  variableRules: Rule[];
  /** Comparison rules: each grounds into constraint rows. */
  constraintRules: Rule[];
  /** The cost categories, in order; the objective is their sum. */
  costTerms: { name: string; expr: Expr }[];
  /**
   * The relations these rules are grounded against — the fixed data tables plus
   * the decision relations the *variable rules themselves* declare. It travels
   * with the program so grounding can never disagree with the checking that
   * produced it (and so the LMP worker gets it along with the rules).
   */
  catalog: Catalog;
}

/** A whole rule set, compiled. */
export interface CompiledRuleSet {
  rules: CompiledRule[];
  costs: CompiledCost[];
  /** The enabled, error-free subset — what gets grounded. */
  program: RuleProgram;
  /**
   * The catalog everything here was checked against. The panel re-checks a draft
   * against it while you type, so a half-edited rule is judged by the same
   * relations the committed ones declare.
   */
  catalog: Catalog;
}

/** The objective's fixed head: `minimize cost`. Only its terms are editable. */
export const OBJECTIVE_SENSE = 'minimize';
export const OBJECTIVE_NAME = 'cost';

/**
 * The objective a program's cost terms add up to. With no terms — all of them
 * disabled, deleted or mid-edit — that's an explicit zero: every feasible point
 * is optimal, which is honest, rather than quietly solving the last good one.
 */
export function objectiveOf(program: RuleProgram): Objective {
  const expr = program.costTerms.length
    ? program.costTerms.map((term) => term.expr).reduce((a, b) => ({ kind: 'binary', op: '+', left: a, right: b }))
    : num(0);
  return makeObjective(OBJECTIVE_SENSE, OBJECTIVE_NAME, expr);
}

/** A ranged body's subject relation — the variable class the rule declares. */
export function declaredRelation(rule: Rule): string | undefined {
  const body = rule.body;
  if (body.kind !== 'range' || body.expr.kind !== 'apply') return undefined;
  return body.expr.relation;
}

/** Whether a rule declares variables (ranged body) rather than constraint rows. */
export const isVariableRule = (rule: Rule): boolean => rule.body.kind === 'range';

/** Whether a rule defines derived data (a bare-expression body). */
export const isDerivedRule = (rule: Rule): boolean => rule.body.kind === 'define';

/**
 * A failure to parse, as a diagnostic: a {@link ParseError} knows where it went
 * wrong, so the message lands on the offending token rather than on the rule.
 */
const parseDiagnostic = (err: unknown): Diagnostic =>
  err instanceof ParseError
    ? { message: err.message, span: err.span }
    : { message: err instanceof Error ? err.message : String(err) };

/** Fill in `errors` from `diagnostics`, so the two can never disagree. */
const reporting = <T extends { diagnostics: Diagnostic[] }>(entry: T): T & { errors: string[] } => ({
  ...entry,
  errors: messagesOf(entry.diagnostics),
});

/** Add a diagnostic to an already-compiled entry, keeping `errors` in step. */
const alsoFlag = <T extends Compiled>(entry: T, message: string, span?: Span): T =>
  reporting({ ...entry, diagnostics: [...entry.diagnostics, { message, span: span ?? entry.nameSpan }] });

/**
 * Parse and validate one rule's text. Never throws — errors come back in the
 * result. The name comes from the text; the entry's cached one is only a
 * fallback for while the text doesn't parse.
 */
export function compileRule({ id, name, source, enabled }: RuleSource, catalog: Catalog): CompiledRule {
  let parsed: ReturnType<typeof parseRuleWithSpans>;
  try {
    parsed = parseRuleWithSpans(source);
  } catch (err) {
    return reporting({ id, name, source, enabled, diagnostics: [parseDiagnostic(err)] });
  }
  const { rule, spans, nameSpan } = parsed;
  const diagnostics = locate(validateRuleDiagnostics(rule, catalog), spans, nameSpan);
  const compiled = { id, name: rule.name, source, enabled, nameSpan };
  if (diagnostics.length) return reporting({ ...compiled, diagnostics });
  return reporting({
    ...compiled,
    rule,
    diagnostics,
    declares: declaredRelation(rule),
    defines: isDerivedRule(rule) ? rule.name : undefined,
  });
}

/** Parse and validate one cost category. Never throws. */
export function compileCost({ id, name, source, enabled }: CostSource, catalog: Catalog): CompiledCost {
  let parsed: ReturnType<typeof parseCostTermWithSpans>;
  try {
    parsed = parseCostTermWithSpans(source);
  } catch (err) {
    return reporting({ id, name, source, enabled, diagnostics: [parseDiagnostic(err)] });
  }
  const { term, spans, nameSpan } = parsed;
  // A cost term is quantifier-free at the top level, so it validates as a rule
  // with no quantifiers: every relation reference and `.attr` read still gets
  // checked, and any sum inside binds its own variables.
  const diagnostics = locate(
    validateRuleDiagnostics(
      { name: term.name, quantifiers: [], body: { kind: 'cmp', op: '=', left: term.expr, right: num(0) } },
      catalog,
    ),
    spans,
    nameSpan,
  );
  const compiled = { id, name: term.name, source, enabled, nameSpan };
  if (diagnostics.length) return reporting({ ...compiled, diagnostics });
  return reporting({ ...compiled, expr: term.expr, diagnostics });
}

/**
 * Flag *every* entry of a name that more than one entry has. Names are the
 * grounded rows' group, so two rules sharing one would silently merge into a
 * single table.
 *
 * All of them, not just the later ones: a collision is symmetric, and nothing
 * about list order makes the first one right. Letting the first keep the name
 * would also mean renaming a rule silently broke a different rule further down
 * — the error landing on the entry you weren't editing. So the whole group is
 * reported and the whole group stays out of the model, and the name is yours
 * again as soon as one of them gives it up.
 */
function flagDuplicateNames<T extends { name: string } & Compiled>(entries: T[], noun: string): T[] {
  const counts = new Map<string, number>();
  for (const entry of entries) counts.set(entry.name, (counts.get(entry.name) ?? 0) + 1);
  return entries.map((entry) =>
    counts.get(entry.name)! > 1
      ? alsoFlag(entry, `more than one ${noun} is named '${entry.name}'`)
      : entry,
  );
}

/**
 * Flag a rule that takes the name of an input relation. The catalog is keyed by
 * name, so without this the rule's relation and the app's would be the same
 * entry — one schema replacing the other, one set of rows standing in for the
 * other's — with nothing to say which the rest of the rules meant.
 */
function flagShadowedRelations(rules: CompiledRule[], dataCatalog: Catalog): CompiledRule[] {
  return rules.map((rule) => {
    const relation = rule.defines ?? rule.declares;
    if (!relation || !dataCatalog[relation]) return rule;
    return alsoFlag(rule, `'${relation}' is an input relation; a rule can't redefine it`);
  });
}

/** Parse a rule, or nothing if it doesn't parse. Used to learn what it declares. */
function tryParse(entry: RuleSource): Rule | undefined {
  try {
    return parseRule(entry.source);
  } catch {
    return undefined;
  }
}

/**
 * Compile a whole rule set. The resulting {@link CompiledRuleSet.program} holds
 * only the entries that are enabled *and* error-free, so the model stays
 * solvable while one of them is being typed.
 *
 * Two passes, because the rules describe their own vocabulary: parsing them
 * first reveals which decision relations they declare and with what keys, and
 * only then can anything be checked against those relations. So widening `SOC`
 * to a third index in its bounds rule is a legal edit that every other rule
 * mentioning `SOC` is immediately re-checked against — rather than a fixed arity
 * the rules are measured by.
 *
 * `dataCatalog` is the fixed half: the entity tables and data lookups the app
 * fills in, which no rule can redefine into existence.
 */
export function compileRuleSet(src: RuleSetSource, dataCatalog: Catalog = GRID_DATA_CATALOG): CompiledRuleSet {
  const parsed = src.rules.map(tryParse).filter((r): r is Rule => !!r);
  // Two things are read off the rules themselves: the relations they declare,
  // and — for the ones they compute — the type of what they compute.
  const catalog = typedCatalog(catalogFor(parsed, dataCatalog), parsed.filter(isDerivedRule));

  const rules = flagShadowedRelations(
    flagDuplicateNames(src.rules.map((rule) => compileRule(rule, catalog)), 'rule'),
    dataCatalog,
  );
  const costs = flagDuplicateNames(src.costs.map((cost) => compileCost(cost, catalog)), 'cost category');

  const active = rules.filter((r) => r.enabled && r.rule && r.errors.length === 0).map((r) => r.rule!);
  return {
    rules,
    costs,
    catalog,
    program: {
      derivedRules: active.filter(isDerivedRule),
      variableRules: active.filter(isVariableRule),
      constraintRules: active.filter((rule) => !isVariableRule(rule) && !isDerivedRule(rule)),
      costTerms: costs
        .filter((c) => c.enabled && c.expr && c.errors.length === 0)
        .map((c) => ({ name: c.name, expr: c.expr! })),
      catalog,
    },
  };
}

/**
 * The errors an entry's draft text would have if it were applied: the whole set
 * recompiled with that entry's text swapped in, then its own errors read back.
 *
 * {@link compileRule} on the draft alone is cheaper, but it can only see what's
 * inside the one entry. The checks that are about the *set* — a name another
 * entry already has, a relation the app supplies — would then wait for the
 * commit, and a name is exactly the thing worth being told about while it's
 * still being typed.
 */
export function draftDiagnostics(
  set: CompiledRuleSet,
  kind: 'rules' | 'costs',
  id: string,
  source: string,
  dataCatalog: Catalog = GRID_DATA_CATALOG,
): Diagnostic[] {
  const swap = <T extends SourceEntry>(entries: T[], apply: boolean): SourceEntry[] =>
    apply ? entries.map((e) => (e.id === id ? { ...e, source } : e)) : entries;
  const compiled = compileRuleSet(
    {
      rules: swap(set.rules, kind === 'rules'),
      costs: swap(set.costs, kind === 'costs'),
    },
    dataCatalog,
  );
  return compiled[kind].find((entry) => entry.id === id)?.diagnostics ?? [];
}

/** {@link draftDiagnostics} as plain messages. */
export function draftErrors(
  set: CompiledRuleSet,
  kind: 'rules' | 'costs',
  id: string,
  source: string,
): string[] {
  return messagesOf(draftDiagnostics(set, kind, id, source));
}

/**
 * The full input data a program sees: the base relations plus everything its
 * definition rules compute from them. Exported because the DATA tables show a
 * derived relation alongside the base ones, and shouldn't have to solve to do it.
 */
export function groundData(program: RuleProgram, base: Data): Data {
  return groundDerived(program.derivedRules, base, program.catalog);
}

/**
 * Ground a compiled program against the data — the model the solver consumes.
 *
 * The cost categories are grounded one at a time and then summed, so the model
 * carries both what the solver needs (one flat coefficient map) and what the UI
 * needs (which category each coefficient came from).
 */
export function groundProgram(program: RuleProgram, base: Data): Model {
  const catalog = program.catalog;
  // Derived relations first: everything downstream reads them as ordinary data.
  const data = groundDerived(program.derivedRules, base, catalog);
  const variables = groundVariables(program.variableRules, data, catalog);
  const exists = new Set(variables.map((v) => v.name));

  const terms: ObjectiveTermGroup[] = program.costTerms.map((term) => ({
    name: term.name,
    coefficients: groundObjective(
      makeObjective(OBJECTIVE_SENSE, term.name, term.expr),
      data,
      catalog,
      exists,
    ).coefficients,
  }));

  const coefficients = new Map<string, number>();
  for (const term of terms) {
    for (const [name, coefficient] of term.coefficients) {
      coefficients.set(name, (coefficients.get(name) ?? 0) + coefficient);
    }
  }

  return {
    variables,
    constraints: groundRules(program.constraintRules, data, catalog, exists),
    objective: { sense: OBJECTIVE_SENSE, coefficients, terms },
  };
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/**
 * The starting rule set: the hand-authored rules and cost categories, rendered
 * to text. The authored ASTs in constraintRules.ts stay the definition of the
 * *default* problem; from here on the text is what the user edits.
 */
export function defaultRuleSetSource(requireMatchingSOC = false): RuleSetSource {
  return {
    // An entry's id is the name it was created with — readable and stable, and
    // it stays put if the name in the text is later changed. Suffixed when
    // taken, since an id has to be the one unique thing an edit is addressed to
    // even while two entries transiently share a name.
    rules: ALL_RULES.reduce<RuleSource[]>((entries, rule) => {
      entries.push({
        id: uniqueRuleName(entries, rule.name),
        name: rule.name,
        source: ruleToString(rule.name === FINAL_SOC_RULE ? finalSocFor(requireMatchingSOC) : rule),
        enabled: true,
      });
      return entries;
    }, []),
    costs: COST_TERMS.map((term) => ({
      id: term.name,
      name: term.name,
      source: costTermToString(term),
      enabled: true,
    })),
  };
}

/** The final-SOC variant the "batteries end where they began" toggle selects. */
export function finalSocFor(requireMatchingSOC: boolean): Rule {
  return requireMatchingSOC ? finalSocMatchRule : finalSocRule;
}

/** The name given to the `final_soc` rule, which the SOC toggle rewrites. */
export const FINAL_SOC_RULE = 'final_soc';

/**
 * The rule a freshly added entry starts from — valid, harmless (it grounds to a
 * bound that always holds), and a worked example of the syntax to edit: a
 * parameter, a generator expression, and the name in the `def` head. Rename it
 * by editing the text.
 */
export const newRuleSource = (name: string): string =>
  `def ${name}(t: timestep):\n  sum(P_gen(g, t) for g in gen) >= 0`;

/**
 * A freshly added cost category: a small per-MWh adder on generation. Valid, and
 * it grounds to real rows so the new category's table isn't empty while you edit.
 */
export const newCostSource = (name: string): string =>
  `def ${name}():\n  sum(0.01 * P_gen(g, t) for g in gen for t in timestep)`;

/** The default names new entries are created with. */
export const NEW_RULE_NAME = 'new_rule';
export const NEW_COST_NAME = 'new_cost';

/**
 * A name free of both the names and the ids in `entries`, derived from `base`
 * (`new_rule`, `new_rule_2`, …). Ids are checked too because a new entry's id is
 * its starting name, and ids have to stay unique for edits to land correctly.
 */
export function uniqueRuleName(entries: SourceEntry[], base: string): string {
  const taken = new Set(entries.flatMap((e) => [e.name, e.id]));
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base}_${n}`;
    if (!taken.has(candidate)) return candidate;
  }
}
